<script setup lang="ts">
import {
    AlertCircle,
    Download,
    Loader2,
    Minus,
    TrendingDown,
    TrendingUp
} from 'lucide-vue-next';
import { ref, watch } from 'vue';

import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@/components/ui/card';
import { downloadBlob } from '@/lib/commonFunctions';
import {
    downloadElqanoAdminReport,
    downloadGoogleAdsConversions,
    getElqanoAdminReportKpis,
    type ReportKPIs
} from '@/services/elqano_admin_report';
import { useTenantStore } from '@/stores/tenant';

const tenantStore = useTenantStore();

const downloading = ref(false);
const downloadingConversions = ref(false);
const error = ref<string | null>(null);

const kpis = ref<ReportKPIs | null>(null);
const kpisLoading = ref(false);
const kpisError = ref<string | null>(null);

const fetchKpis = async () => {
    const tenantId = tenantStore.tenantId;
    if (!tenantId) return;

    kpisLoading.value = true;
    kpisError.value = null;

    try {
        kpis.value = await getElqanoAdminReportKpis(tenantId);
    } catch (err) {
        kpisError.value =
            err instanceof Error ? err.message : 'Failed to load KPIs';
        kpis.value = null;
    } finally {
        kpisLoading.value = false;
    }
};

watch(
    () => tenantStore.tenantId,
    (tenantId) => {
        kpis.value = null;
        kpisError.value = null;
        if (!tenantId) return;
        fetchKpis();
    },
    { immediate: true }
);

const formatCount = (value: number) => value.toLocaleString('fr-FR');

const trendIcon = (current: number, previous: number) => {
    if (current > previous) return TrendingUp;
    if (current < previous) return TrendingDown;
    return Minus;
};

const countDeltaLabel = (current: number, previous: number) => {
    const diff = current - previous;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff} vs previous 7 days (${previous})`;
};

const percentTrendIcon = (current: number | null, previous: number | null) => {
    if (current === null || previous === null) return Minus;
    return trendIcon(current, previous);
};

const percentDeltaLabel = (current: number | null, previous: number | null) => {
    if (current === null || previous === null) return 'Not enough data';
    const diff = Math.round((current - previous) * 10) / 10;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff} vs previous 7 days (${previous}%)`;
};

const handleDownloadConversions = async () => {
    const tenantId = tenantStore.tenantId;
    if (!tenantId || downloadingConversions.value) return;

    const date = new Date().toISOString().split('T')[0];
    downloadingConversions.value = true;
    error.value = null;

    try {
        const blob = await downloadGoogleAdsConversions(tenantId);
        downloadBlob(blob, `google_ads_conversions_${date}.csv`);
    } catch (err) {
        error.value =
            err instanceof Error
                ? err.message
                : 'Failed to download conversions';
    } finally {
        downloadingConversions.value = false;
    }
};

const handleDownload = async () => {
    const tenantId = tenantStore.tenantId;
    if (!tenantId || downloading.value) return;

    const date = new Date().toISOString().split('T')[0];
    downloading.value = true;
    error.value = null;

    try {
        const blob = await downloadElqanoAdminReport(tenantId);
        downloadBlob(blob, `elqano_admin_report_${date}.xlsx`);
    } catch (err) {
        error.value =
            err instanceof Error ? err.message : 'Failed to download report';
    } finally {
        downloading.value = false;
    }
};
</script>

<template>
    <div class="flex flex-col gap-6 min-h-full">
        <div class="flex items-start justify-between gap-4">
            <div class="flex flex-col gap-2">
                <h2 class="text-xl font-semibold">Reports</h2>
                <p class="text-sm text-muted-foreground">
                    Usage and subscription metrics across all Freemium
                    subscribed tenants.
                </p>
            </div>
            <div class="flex items-center gap-2 shrink-0">
                <Button
                    variant="outline"
                    size="sm"
                    class="gap-2"
                    :disabled="downloadingConversions"
                    @click="handleDownloadConversions"
                >
                    <Loader2
                        v-if="downloadingConversions"
                        class="size-4 animate-spin"
                    />
                    <Download v-else class="size-4" />
                    Download Google Ads conversions report
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    class="gap-2"
                    :disabled="downloading"
                    @click="handleDownload"
                >
                    <Loader2 v-if="downloading" class="size-4 animate-spin" />
                    <Download v-else class="size-4" />
                    Download
                </Button>
            </div>
        </div>

        <div
            v-if="error"
            class="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
            <AlertCircle class="size-4 shrink-0" />
            {{ error }}
        </div>

        <div
            v-if="kpisError"
            class="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
            <AlertCircle class="size-4 shrink-0" />
            {{ kpisError }}
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
                <CardHeader class="pb-2">
                    <CardDescription>Accounts created</CardDescription>
                    <CardTitle class="text-3xl">
                        <template v-if="kpisLoading">…</template>
                        <template v-else>{{
                            kpis
                                ? formatCount(kpis.accounts_created.current)
                                : '-'
                        }}</template>
                    </CardTitle>
                </CardHeader>
                <CardContent
                    class="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                    <template v-if="kpis">
                        <component
                            :is="
                                trendIcon(
                                    kpis.accounts_created.current,
                                    kpis.accounts_created.previous
                                )
                            "
                            class="size-3.5 shrink-0"
                        />
                        <span>{{
                            countDeltaLabel(
                                kpis.accounts_created.current,
                                kpis.accounts_created.previous
                            )
                        }}</span>
                    </template>
                </CardContent>
            </Card>

            <Card>
                <CardHeader class="pb-2">
                    <CardDescription>Pending onboarding</CardDescription>
                    <CardTitle class="text-3xl">
                        <template v-if="kpisLoading">…</template>
                        <template v-else>{{
                            kpis?.onboarding_pending_percent.current != null
                                ? `${kpis.onboarding_pending_percent.current}%`
                                : '-'
                        }}</template>
                    </CardTitle>
                </CardHeader>
                <CardContent
                    class="flex items-center gap-1.5 text-xs text-muted-foreground"
                >
                    <template v-if="kpis">
                        <component
                            :is="
                                percentTrendIcon(
                                    kpis.onboarding_pending_percent.current,
                                    kpis.onboarding_pending_percent.previous
                                )
                            "
                            class="size-3.5 shrink-0"
                        />
                        <span>{{
                            percentDeltaLabel(
                                kpis.onboarding_pending_percent.current,
                                kpis.onboarding_pending_percent.previous
                            )
                        }}</span>
                    </template>
                </CardContent>
            </Card>

            <Card>
                <CardHeader class="pb-2">
                    <CardDescription>Activated accounts</CardDescription>
                    <CardTitle class="text-3xl">
                        <template v-if="kpisLoading">…</template>
                        <template v-else>{{
                            kpis
                                ? formatCount(
                                      kpis.accounts_with_multiple_connections
                                  )
                                : '-'
                        }}</template>
                    </CardTitle>
                </CardHeader>

                <CardContent class="text-xs text-muted-foreground">
                    Several connections and messages.
                </CardContent>
            </Card>

            <Card>
                <CardHeader class="pb-2">
                    <CardDescription>Active accounts</CardDescription>
                    <CardTitle class="text-3xl">
                        <template v-if="kpisLoading">…</template>
                        <template v-else>{{
                            kpis ? formatCount(kpis.active_accounts) : '-'
                        }}</template>
                    </CardTitle>
                </CardHeader>
                <CardContent class="text-xs text-muted-foreground">
                    Paid accounts
                </CardContent>
            </Card>
        </div>
    </div>
</template>
